import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { initDocLang } from '../lib/docLang';
import axios from 'axios';
import { toast } from 'sonner';
import i18n, { SUPPORTED_LANGUAGES, applyOverrides } from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Apply the user's saved preferred_language to the i18n runtime so the
 *  entire tree re-renders in that locale on login / profile refresh. */
const syncLanguageFromUser = (userData) => {
  initDocLang(userData);
  const lng = (userData?.preferred_language || 'en').toLowerCase();
  if (SUPPORTED_LANGUAGES.includes(lng) && i18n.language !== lng) {
    i18n.changeLanguage(lng);
  }
};

/** Fetch admin-defined translation overrides and merge them into i18next.
 *  Silent on failure — falls back to the static bundle. */
const loadOverrides = async () => {
  try {
    const res = await axios.get(`${API}/i18n/overrides`);
    applyOverrides(res.data);
  } catch {
    /* offline / not signed in — keep static defaults */
  }
};

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // Bumped whenever any entity (employee/customer/project/...) is mutated.
  // Components fetching their own copies of these collections include this
  // in their useEffect deps to auto-refresh.
  const [dataVersion, setDataVersion] = useState(0);

  const bumpDataVersion = useCallback(() => {
    setDataVersion((v) => v + 1);
  }, []);

  const clearAuth = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    // Clear any "view as role" preview state so the next login starts
    // with the user's real permissions.
    try { sessionStorage.removeItem('expertfix:view_as_role'); } catch { /* ignore */ }
    // Per-person display currency lives on the account; don't hand it to the next login on this browser.
    try { localStorage.removeItem('expertfix_display_currency'); } catch { /* ignore */ }
    // Per-person list filters/searches must not carry over to the next login on this browser.
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('customer-invoices.') || k.startsWith('employee-invoices.'))
        .forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore */ }
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // 401 interceptor — skip for login/register endpoints
  // Also: auto-bump dataVersion after successful PUT/POST/DELETE on core
  // entity endpoints so components fetching their own copies refresh.
  useEffect(() => {
    const REFRESHABLE_PATHS = [
      // Core entities
      '/employees', '/customers', '/projects', '/users',
      // Finance / billing
      '/invoices', '/employee-invoices', '/expenses',
      // Time tracking
      '/timesheets', '/weekly-timesheets',
      // Inventory & assets
      '/inventory',
      // ISO 9001 quality module
      '/iso/documents', '/iso/audits', '/iso/ncrs', '/iso/capas',
      '/iso/ratings', '/iso/training',
      // Electronic checklists & test reports
      '/checklists', '/test-reports',
      // Manpower / planning
      '/manpower',
      // Roles & permissions
      '/roles', '/role-permissions',
      // Settings & company profile (so dropdown changes propagate)
      '/settings', '/company',
    ];
    const isMutating = (cfg) => {
      const m = (cfg?.method || '').toLowerCase();
      return m === 'put' || m === 'post' || m === 'delete' || m === 'patch';
    };
    const matchesRefreshable = (url) => {
      if (!url) return false;
      return REFRESHABLE_PATHS.some((p) => url.includes(`/api${p}`));
    };

    const responseInt = axios.interceptors.response.use(
      (response) => {
        try {
          if (isMutating(response.config) && matchesRefreshable(response.config.url)) {
            setDataVersion((v) => v + 1);
          }
        } catch { /* never block successful response */ }
        return response;
      },
      (error) => {
        const url = error.config?.url || '';
        const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
        if (error.response?.status === 401 && !isAuthEndpoint && localStorage.getItem('token')) {
          clearAuth();
          toast.error('Session expired. Please log in again.');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(responseInt);
  }, [clearAuth]);

  // On mount / token change: validate token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`)
        .then((res) => { setUser(res.data); syncLanguageFromUser(res.data); loadOverrides(); })
        .catch(() => { clearAuth(); })
        .finally(() => { setLoading(false); });
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    }
  }, [token, clearAuth]);

  const login = async (username, password) => {
    try {
      // Remove any stale auth header before login attempt
      delete axios.defaults.headers.common['Authorization'];
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token, user: userData } = response.data;

      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      syncLanguageFromUser(userData);
      loadOverrides();

      toast.success('Login successful!');
      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(errorMessage);
      return false;
    }
  };

  const logout = () => {
    clearAuth();
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!token && !!user,
    dataVersion,
    bumpDataVersion,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
